import React, { useCallback } from 'react';
import { useField, useFormikContext } from 'formik';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Label } from '@/components/ui/label';
import { VariableTextarea } from '@/components/ui/variable-textarea';

import { useFormikError } from '@/lib/error';
import { extractPromptVariables } from '@/lib/prompt';
import { cn } from '@/lib/utils';

type PostCallPromptProps = {
  title: string;
  description: string;
  required?: boolean;
  name: string;
  variablesName: string;
  promptVariables: string[];
  inputId?: string;
  promptLabel?: React.ReactNode;
  promptDescription?: React.ReactNode;
  promptPlaceholder?: string;
  promptSectionClassName?: string;
  textareaClassName?: string;
  children?: React.ReactNode;
};

export const PostCallPrompt = React.memo(
  ({
    title,
    description,
    required = false,
    name,
    variablesName,
    promptVariables,
    inputId = name.replace(/_/g, '-'),
    promptLabel = 'Prompt',
    promptDescription,
    promptPlaceholder,
    promptSectionClassName,
    textareaClassName,
    children,
  }: PostCallPromptProps) => {
    const [, , helpers] = useField<string>(name);
    const [, varsMeta, varsHelpers] = useField<string[]>(variablesName);
    const { getFieldProps } = useFormikContext();

    const onChange = useCallback(
      (value: string) => {
        helpers.setValue(value);
        varsHelpers.setValue(extractPromptVariables(value));
      },
      [helpers.setValue, varsHelpers.setValue]
    );

    const hasError = useFormikError();

    const variableError = typeof varsMeta.error === 'string' ? varsMeta.error : undefined;

    return (
      <Card className="bg-muted rounded-2xl gap-0 p-1 border-0 shadow-none">
        <CardHeader className="px-5 py-3">
          <CardTitle className="text-sm font-semibold">
            {required ? <RequiredLabel>{title}</RequiredLabel> : title}
          </CardTitle>
          <p className="text-sm text-muted-foreground">{description}</p>
        </CardHeader>
        <CardContent className="bg-card rounded-xl shadow-sm p-5 space-y-4">
          <Field aria-invalid={hasError(name)}>
            <div className="space-y-6">
              <div className={cn('space-y-2', promptSectionClassName)}>
                {promptDescription ? (
                  <div className="flex flex-col gap-1">
                    <Label htmlFor={inputId}>{promptLabel}</Label>
                    <p className="text-sm text-muted-foreground">{promptDescription}</p>
                  </div>
                ) : (
                  <Label htmlFor={inputId}>{promptLabel}</Label>
                )}
                <VariableTextarea
                  id={inputId}
                  className={cn('min-h-32 max-h-[500px] overflow-y-auto', textareaClassName)}
                  onValueChange={onChange}
                  placeholder={promptPlaceholder}
                  dropdownEnabled={true}
                  variables={promptVariables}
                  aria-invalid={hasError(name)}
                  {...getFieldProps(name)}
                />
                <p className="text-xs text-muted-foreground">
                  Type{' '}
                  <span className="font-medium py-px px-1 border rounded-lg text-foreground bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-800">
                    {'{{'}
                  </span>{' '}
                  to add variables
                </p>
                <ErrorField name={name} />
                {variableError && <p className="text-sm text-destructive">{variableError}</p>}
              </div>
              {children}
            </div>
          </Field>
        </CardContent>
      </Card>
    );
  }
);
